import { isEmpty } from 'lodash';
import uniqueId from 'lodash/uniqueId';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import React, {
  useReducer,
  useEffect,
  useContext,
  useRef,
  createContext,
  useState,
} from 'react';
import ReactDOM from 'react-dom';

import markerUrl from '../../assets/images/map-marker.svg';
import * as styles from './Map.module.scss';

mapboxgl.accessToken = process.env.GATSBY_MAPBOX_TOKEN;

export const MapContext = createContext(null);

const initialState = {
  map: null,
  markers: [],
  popup: null,
  isInit: false,
};

function reducer(state, action) {
  if (process.env.NODE_ENV === 'development') {
    console.info(action);
  }

  switch (action.type) {
    case 'INIT':
      return {
        ...state,
        isInit: true,
      };

    case 'SET_MAP':
      return {
        ...state,
        ...action.payload,
      };
    case 'ADD_MARKER':
      return {
        ...state,
        markers: [ ...state.markers, action.payload.marker ],
      };
    case 'REMOVE_MARKER':
      return {
        ...state,
        markers: state.markers.filter(
          (marker) => marker.id !== action.payload.marker.id
        ),
      };
    case 'POPUP_OPEN':
      return {
        ...state,
        popup: action.payload.popup,
      };
    case 'POPUP_CLOSE':
      return {
        ...state,
        popup: null,
      };
    default:
      return state;
  }
}

export const Map = ({ zoom, center, children, ...other }) => {
  const ref = useRef(null);
  const [ state, dispatch ] = useReducer(reducer, initialState);
  const { map, markers, popup, isInit } = state;
  const otherProps = !isEmpty(other) ? other : null;

  useEffect(() => {
    if (!ref.current) return;

    const options = {
      container: ref.current,
      style: 'mapbox://styles/medicalimaging/ckrgc621d596a19nz2jy07mo8',
      center: center || [ -3.2872908, 53.2969611 ],
      zoom: zoom || 5,
      ...otherProps,
    };

    if (center) {
      options.center = center;
    }

    const map = new mapboxgl.Map(options);
    
    map.addControl(new mapboxgl.NavigationControl(), 'bottom-left');
    map.scrollZoom.disable();

    dispatch({ type: 'SET_MAP', payload: { map } });

    return () => {
      map.remove();
    };
  }, [ ref, dispatch, zoom, center, otherProps ]);

  useEffect(() => {
    if (!map || !markers.length) return;

    const animSpeed = isInit ? 1200 : 0;

    if (markers.length > 1) {
      const coordinates = markers.map(({ marker }) => marker.getLngLat());
      const bounds = coordinates.reduce(function (bounds, coord) {
        return bounds.extend(coord);
      }, new mapboxgl.LngLatBounds(coordinates[ 0 ], coordinates[ 0 ]));

      map.fitBounds(bounds, {
        padding: 100,
        duration: animSpeed,
      });
    } else if (markers.length === 1) {
      map.flyTo({
        center: markers[ 0 ].marker.getLngLat(),
        zoom: 12,
        speed: animSpeed,
      });
    }

    if (!isInit) {
      dispatch({ type: 'INIT' });
    }
  }, [ dispatch, markers, map, isInit ]);

  useEffect(() => {
    if (!popup || !map) return;

    const getPos = (lngLat) => {
      if (!popup) return lngLat;
      const px = map.project(lngLat);
      px.y -= popup.getElement()?.clientHeight / 2;
      return !px.y ? lngLat : map.unproject(px);
    };

    map.flyTo({
      center: getPos(popup.getLngLat()),
      // speed: isInit ? 0 : 1.2
    });
  }, [ popup, map, isInit ]);

  return (
    <div className={styles.container} ref={ref}>
      <MapContext.Provider value={{ state, dispatch }}>
        {children}
      </MapContext.Provider>
    </div>
  );
};

export const Marker = ({ coords, popup, isPopupOpen, counter }) => {
  const [ marker, setMarker ] = useState(null);
  const {
    state: { map },
    dispatch,
  } = useContext(MapContext);

  useEffect(() => {
    if (!map) return;

    const element = document.createElement('div');

    ReactDOM.render(
      <div className={styles.marker}>
        <img src={markerUrl} alt="" />
        {counter && <span className={styles.counter}>{counter}</span>}
      </div>,
      element
    );

    const marker = {
      id: uniqueId(),
      marker: new mapboxgl.Marker({ element, anchor: 'bottom' }).setLngLat(
        coords
      ),
    };

    if (popup) {
      const placeholder = document.createElement('div');
      ReactDOM.render(popup, placeholder);

      const mapPopup = new mapboxgl.Popup({
        className: styles.popup,
        anchor: 'bottom',
        offset: 45,
        focusAfterOpen: false
      })
        .setLngLat(coords)
        .setDOMContent(placeholder)
        .setMaxWidth('300px');

      mapPopup.on('open', () =>
        dispatch({ type: 'POPUP_OPEN', payload: { popup: mapPopup } })
      );
      mapPopup.on('close', () => dispatch({ type: 'POPUP_CLOSE' }));

      marker.marker.setPopup(mapPopup);
    }

    marker.marker.addTo(map);

    setMarker(marker);
    dispatch({ type: 'ADD_MARKER', payload: { marker } });

    return () => {
      if (marker) {
        marker.marker.remove();
        dispatch({ type: 'REMOVE_MARKER', payload: { marker } });
        setMarker(null);
      }
    };
  }, [ coords, map, popup, dispatch, counter ]);

  useEffect(() => {
    const popup = marker?.marker.getPopup();
    if (!popup) return;

    if (isPopupOpen) {
      popup.addTo(map);
    } else {
      popup.remove();
    }
  }, [ isPopupOpen, marker, map ]);

  return null;
};

export default Map;
